const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  return false;
};

export const required =
  (label = "This field") =>
  (value) =>
    isEmpty(value) ? `${label} is required` : null;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Required + basic email format (aligned with fortify_user_portal usage). */
export const validateEmail = (value, { label = "Email" } = {}) => {
  const r = required(label)(value);
  if (r) return r;
  return EMAIL.test(String(value).trim()) ? null : "Please enter a valid email";
};

export const PATTERNS = {
  digitsOnly: /^\d+$/,
  nameChars: /^[A-Za-z][A-Za-z' -]*$/,
  lowercase: /[a-z]/,
  uppercase: /[A-Z]/,
  digit: /\d/,
  special: /[!@#$%^&*()\-_=+[\]{};':"\\|,.<>/?`~]/,
};

/** Person name — letters, spaces, hyphens, apostrophes; length-bounded. */
export const validateName = (
  value,
  { label = "Name", min = 2, max = 50 } = {}
) => {
  const r = required(label)(value);
  if (r) return r;
  const trimmed = value.trim();
  if (trimmed.length < min) return `${label} must be at least ${min} characters`;
  if (trimmed.length > max) return `${label} must be at most ${max} characters`;
  if (!PATTERNS.nameChars.test(trimmed)) {
    return `${label} can only contain letters, spaces, hyphens, and apostrophes`;
  }
  return null;
};

/** Phone number — digits-only, length-bounded. */
export const validatePhone = (
  value,
  { label = "Phone number", min = 7, max = 15 } = {}
) => {
  const r = required(label)(value);
  if (r) return r;
  const digits = String(value).replaceAll(/\D+/g, "");
  if (!PATTERNS.digitsOnly.test(digits)) {
    return `${label} must contain digits only`;
  }
  if (digits.length < min) return `${label} must be at least ${min} digits`;
  if (digits.length > max) return `${label} must be at most ${max} digits`;
  return null;
};

/** Strong password: 8+ chars with lower, upper, digit and special character (portal-aligned). */
export const validatePassword = (
  value,
  { label = "Password", min = 8 } = {}
) => {
  const r = required(label)(value);
  if (r) return r;
  if (value.length < min) return `${label} must be at least ${min} characters`;
  if (!PATTERNS.lowercase.test(value))
    return `${label} must include a lowercase letter`;
  if (!PATTERNS.uppercase.test(value))
    return `${label} must include an uppercase letter`;
  if (!PATTERNS.digit.test(value)) return `${label} must include a number`;
  if (!PATTERNS.special.test(value))
    return `${label} must include a special character`;
  return null;
};

export const validateConfirmPassword = (
  confirm,
  password,
  { label = "Confirm Password" } = {}
) => {
  const r = required(label)(confirm);
  if (r) return r;
  return confirm === password ? null : "Passwords do not match";
};

const runValidators = (list, value, allValues) => {
  for (const v of list) {
    if (typeof v !== "function") continue;
    const err = v(value, allValues);
    if (err) return err;
  }
  return null;
};

export const validateForm = (values, schema) => {
  const errors = {};
  for (const [field, rule] of Object.entries(schema)) {
    const list = Array.isArray(rule) ? rule : [rule];
    const err = runValidators(list, values[field], values);
    if (err) errors[field] = err;
  }
  return errors;
};
